<template>
  <div
    v-bind="$attrs"
    :class="[
      'own-tag',
      `own-tag--variant-${variant}`,
      `own-tag--size-${size}`,
      { 'own-tag--clickable': !!onClick },
    ]"
    @click="handleClick"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <Component
      :is="icon"
      v-if="icon"
      class="flex-shrink-0"
      :size="size === 'medium' ? 16 : 12"
      weight="bold"
    />

    <OwnType
      v-if="displayText"
      :preserve-whitespace="preserveWhitespace"
      :text-wrap="textWrap"
      :text="displayText"
      variant="title-small"
      color="inherit"
      :monospace="monospace"
    />

    <Component
      :is="iconRight"
      v-if="iconRight"
      class="flex-shrink-0"
      :size="size === 'medium' ? 16 : 12"
      weight="bold"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, type Component } from 'vue'

import { OwnType } from '../OwnType'

import { OwnTagSize, OwnTagVariant } from './types'

const rawProps = withDefaults(
  defineProps<{
    icon?: Component
    iconRight?: Component
    text?: string | number
    hoverText?: string | number
    monospace?: boolean
    size?: OwnTagSize
    variant?: OwnTagVariant
    textWrap?: boolean
    preserveWhitespace?: boolean
    onClick?: () => void
  }>(),
  {
    hoverText: undefined,
    icon: undefined,
    iconRight: undefined,
    monospace: false,
    onClick: undefined,
    size: 'medium',
    text: undefined,
    variant: 'neutral',
  }
)

const {
  icon,
  iconRight,
  text,
  hoverText,
  monospace,
  size,
  variant,
  textWrap,
  preserveWhitespace,
  onClick,
} = toRefs(rawProps)

const isHovered = ref(false)

const handleClick = (event: MouseEvent): void => {
  if (!onClick.value) return
  event.stopPropagation()
  onClick.value()
}

const onMouseEnter = (): void => {
  if (hoverText.value !== undefined) {
    isHovered.value = true
  }
}

const onMouseLeave = (): void => {
  if (isHovered.value) {
    isHovered.value = false
  }
}

const displayText = computed(() => {
  if (isHovered.value && hoverText.value !== undefined) {
    return hoverText.value
  }
  return text.value
})
</script>

<style lang="scss">
@mixin define-color($color) {
  background: linear-gradient(
      0deg,
      rgba($color, 0.08) 0%,
      rgba($color, 0.08) 100%
    ),
    #fff;
  color: rgb($color);
}

.own-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &--variant {
    &-brand {
      @include define-color(var(--raw-misc-brand));
    }

    &-neutral {
      @include define-color(var(--raw-text-color-secondary));
    }

    &-success {
      @include define-color(var(--raw-status-success));
    }

    &-danger {
      @include define-color(var(--raw-status-danger));
    }

    &-warning {
      @include define-color(var(--raw-status-warning));
    }
  }

  &--size {
    &-small {
      border-radius: 8px;
      padding: 2px 4px;
      gap: 2px;
    }

    &-medium {
      border-radius: 12px;
      padding: 4px 8px;
      gap: 4px;
    }
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      opacity: 0.5;
    }

    &:active {
      opacity: 0.75;
    }
  }
}
</style>
